<template>
	<div class="relative">
		<!-- Loading indicator -->
		<svg v-if="loadingSlotsFlag" class="fullcalendar-centered-item fullcalendar-loading animate-spin-anticlock h-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
		</svg>
		
		<!-- No event dialog -->
		<div data-cy="no-event-dialog" :style="brandingColor" v-if="!loadingSlotsFlag && noEventFlag" class="fullcalendar-centered-item fullcalendar-no-event p-4 pt-6 flex flex-col text-center items-center">
			<span class="font-bold">No Availability</span>
			<span>Try another week</span>
			<div class="flex justify-between w-full mt-6">
				<button data-cy="no-event-dialog-previous-button" @click.stop="previousWeekClicked" :style="brandingColor" class="previous-week-btn btn btn-outline flex w-40">
					<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
					</svg>
				</button>
				<button data-cy="no-event-dialog-next-button" @click.stop="nextWeekClicked" :style="brandingColor" class="next-week-btn btn btn-outline flex w-40 justify-end">
					<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
					</svg>
				</button>
			</div>
		</div>
		
		<!-- Full calendar el -->
		<div data-cy="visitor-calendar-view-el" :style="brandingColor" id="full-calendar-el"></div>
	</div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex"
import { Calendar } from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import calendarViewSpecific from "../../mixins/calendarViewSpecific.js"
import esLocale from '@fullcalendar/core/locales/es';
import frLocale from '@fullcalendar/core/locales/fr';
import nbLocale from '@fullcalendar/core/locales/nb';
import cnLocale from '@fullcalendar/core/locales/zh-cn';
import deLocale from '@fullcalendar/core/locales/de';
import isLocale from '@fullcalendar/core/locales/is';
import nlLocale from '@fullcalendar/core/locales/nl';
import svLocale from '@fullcalendar/core/locales/sv';
import jaLocale from '@fullcalendar/core/locales/ja';
import csLocale from '@fullcalendar/core/locales/cs';
import arLocale from '@fullcalendar/core/locales/ar';
import plLocale from '@fullcalendar/core/locales/pl';
import ruLocale from '@fullcalendar/core/locales/ru';
import itLocale from '@fullcalendar/core/locales/it';
import fiLocale from '@fullcalendar/core/locales/fi';
import skLocale from '@fullcalendar/core/locales/sk';
import elLocale from '@fullcalendar/core/locales/el';
import daLocale from '@fullcalendar/core/locales/da';
import roLocale from '@fullcalendar/core/locales/ro';
import {localization} from "../../mixins/localization.js"

export default {
	mixins: [calendarViewSpecific, localization],

	name: "FullCalendarView",

	data() {
		return {
			fullCalendar: null,
			noEventFlag: false,
			defaultColors: ["#3498db", "#2ecc71", "#9b59b6", "#34495e", "#e67e22", "#e74c3c", "#95a5a6", "#16a085"],
			defaultCalendarConfigs: {
				weekends: true,
				allDaySlot: false,
				initialView: 'timeGridWeek',
				slotDuration: '00:30:00', 
				scrollTime: '08:00:00',
				plugins: [ 
					timeGridPlugin, 
					interactionPlugin,
					momentTimezonePlugin
				],
				displayEventTime: false,
				headerToolbar: {left: 'prev', center: 'title', right: 'next'}
			},
			manualSettingsConfigs: {
				editable: false,
				eventClick: this.handleEventClick,
			},
			fullCalendarLanguagePacks: {
				en: "en",
				cn: "zh-cn",
				es: "es",
				sp: "es",
				no: "nb",
				fr: "fr",
				is: "is",
				de: "de",
				nl: "nl",
				se: "sv",
				ja: "ja",
				cs: "cs",
				ar: "ar",
				pl: "pl",
				ru: "ru",
				it: "it",
				fi: "fi",
				sk: "sk",
				el: "el",
				da: "da",
				ro: "ro"
			}
		}
	},

	computed: {
		...mapGetters([
			'calendarData',
			'timezone',
			'schedulingData',
			'brandingData',
			'selectedAppointment',
			'availabilitySlots',
			'loadingSlotsFlag',
			'isTime24'
		]),

		brandingColor() {
			return {
				color: "#000000",
			}
		},

		firstDayOfWeek() {
			return this.calendarData.settings.scheduling.firstDayOfWeek || 0;
		}
	},

	mounted() {
		if (!this.fullCalendar) {
			var calendarEl = document.getElementById('full-calendar-el');

			// Init calendar
			this.fullCalendar = new Calendar(calendarEl, {
				firstDay: this.firstDayOfWeek,
				...this.defaultCalendarConfigs,
				...this.manualSettingsConfigs,
				events: this.getInitialFullCalendarEvents,
				timeZone: this.timezone,
				locales: [ esLocale, frLocale, cnLocale, nbLocale, deLocale, isLocale, nlLocale, svLocale, jaLocale, csLocale, arLocale, plLocale, ruLocale, itLocale, fiLocale, skLocale, elLocale, daLocale, roLocale ],
				locale: this.fullCalendarLanguagePacks[this.activeLocale],
				slotLabelFormat: {
					hour: 'numeric',
					minute: '2-digit',
					hour12: !this.isTime24
				}
			});
			this.fullCalendar.render();
			document.getElementsByClassName("fc-header-toolbar")[0].style = this.getCalendarBg(0.5);
		}
	},

	methods: {
		...mapMutations([
			'setTimezone',
			'setSelectedAppointment',
			'setSelectedSlot',
			'setActiveView',
			'setLoadingSlotsFlag',
			'setAvailabilitySlots',
			'setAvailabilityGeneratedAt'
		]),

		handleEventClick(eventInfo) {
			if (!eventInfo.event) {return}

			// Check if slot is not in past
			if (eventInfo.event.start < moment()) {return;}

			// Checking if the event has appointment id set.
			if (!eventInfo.event.extendedProps) {return}
			if (!eventInfo.event.extendedProps.appointment_uuid) {return}

			let eventAppointment = this.appointmentOptions.find((ap) => {
				return ap.uuid == eventInfo.event.extendedProps.appointment_uuid;
			})

			if (!eventAppointment) {return}

			// Setting selected appointment, the one associated with the event user selected.
			this.setSelectedAppointment(eventAppointment);

			let slot = {
				from: moment(eventInfo.event.startStr).utc().format(),
				to: moment(eventInfo.event.endStr).utc().format(),
				available: true
			}
			
			this.setSelectedSlot(slot);
			this.setActiveView("booking");
		},

		checkOverlap(event) {
			return event.display === "inverse-background"
		},

		previousWeekClicked() {
			if (!this.fullCalendar) { return }

			this.fullCalendar.prev()
		},

		nextWeekClicked() {
			if (!this.fullCalendar) { return }

			this.fullCalendar.next()
		}
	},

	watch: {
		timezone() {
			this.fullCalendar.setOption('timeZone', this.timezone);
			this.fullCalendar.refetchEvents();
		},

		selectedAppointment() {
			if (this.isManualSettings) {
				this.fullCalendar.refetchEvents();
			}
		}
	}
}
</script>

<style>
	.fc-scrollgrid-section-liquid {
		height: 1px !important;
	}
	.fullcalendar-centered-item {
		position: absolute;
		left: 0;
		right: 0;
		top: 25%;
		margin-left: auto;
		margin-right: auto;
	}
	.fullcalendar-loading {
		width: 5%;
	}
	.fullcalendar-no-event {
		width: 45%;
		border-radius: 6px;
		box-shadow: 0px 0px 10px 5px #ccc;
		background: white;
		z-index: 9;
	}
	.previous-week-btn:after {
		content: "Previous Week"
	}
	.next-week-btn:before {
		content: "Next Week"
	}
	#full-calendar-el {
		height: 450px;
		max-width: 900px;
		width: 900px;
		margin: auto;
		margin-top: 10px;
	}
	#full-calendar-el.fc .fc-col-header-cell-cushion,
	.fc-timegrid-slot-label-cushion.fc-scrollgrid-shrink-cushion {
		color: #444;
		font-size: 0.65rem;
	}
	#full-calendar-el.fc .fc-toolbar-chunk button.fc-button {
		background-color: transparent;
		border-color: transparent;
		color: inherit;
		margin-right: 2px;
		font-size: 1rem;
		padding: 0px;
	}
	#full-calendar-el.fc .fc-toolbar-chunk button.fc-button:focus {
		box-shadow: none;
	}
	#full-calendar-el.fc .fc-header-toolbar {
		background-color: #F5F9FA;
		margin-bottom: 0;
		font-size: 0.5rem;
		padding: 0.2rem;
		border: 1px solid #CCC;
		border-bottom-width: 0px;
	}
	#full-calendar-el.fc .fc-bg-event { 
		background-color: rgba(215, 215, 215); 
		cursor: not-allowed;
	}
	#full-calendar-el.fc .fc-non-business { 
		cursor: not-allowed;
	}
	/*
	.fc .fc-button { margin: 0px; padding: 0.2em 0.3em; }
	.fc .fc-button .fc-icon { font-size: 1.2rem; }
	*/
	.fc-theme-standard td, .fc-theme-standard th,
	.fc-theme-standard .fc-scrollgrid { border-color: #EEE; }

	.fc-theme-standard .fc-scrollgrid { border: 1px solid #CCC; }
	.fc .fc-scrollgrid-section > td { border-width: 0px; }
	/* .fc .fc-timegrid-slot-label-cushion { display: block } */
	.fc .fc-timegrid-slot-minor { border-width: 0px; }
	#full-calendar-el.fc .fc-day.disabled  {
		background-color: rgba(215, 215, 215);
		cursor: not-allowed;
	}
	#full-calendar-el.fc .fc-timegrid-event-harness {
		cursor: pointer;
		direction: ltr;
	}
	@media(max-width: 768px) {
		#full-calendar-el {
			max-width: 95vw;
			width: 95vw;
		}
		.fullcalendar-no-event {
			width: 45%;
		}
	}
	@media(max-width: 480px) {
		#full-calendar-el.fc .fc-v-event .fc-event-title {
			font-size: 10px;
		}
		.fullcalendar-loading {
			width: 20%;
		}

		.fullcalendar-no-event {
			width: 70%;
		}
		.previous-week-btn:after {
			content: "Previous"
		}
		.next-week-btn:before {
			content: "Next"
		}
	}
</style>
